import { ScaleElement } from '@scale/base-element';

const CACHE_MAX = 15;

class ScaleQuickView extends ScaleElement {
  static requiredRefs = ['dialog', 'content', 'loading'];

  #cache = new Map();
  #currentHandle = null;
  #abortController = null;

  setup() {
    // Global click delegation for [data-quick-view] buttons
    this.onDocument('click', (e) => {
      const trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      this.load(trigger.dataset.productHandle, trigger.dataset.variantId);
    });

    // Close on cart update (user sees cart drawer instead)
    this.onDocument('shopify:cart:lines-update', (event) => {
      if (event.action !== 'add') return;
      event.promise?.then(() => {
        if (this.ref('dialog').open) this.#close();
      }).catch(() => {});
    });

    // Backdrop click + Escape (close button handled by on:click="close" declarative)
    const dialog = this.ref('dialog');
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.#close();
    }, { signal: this.signal });

    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#close();
    }, { signal: this.signal });
  }

  close() {
    this.#close();
  }

  async load(handle, variantId) {
    if (!handle || !/^[a-z0-9\-]+$/.test(handle)) return;
    this.#currentHandle = handle;

    const content = this.ref('content');
    const loading = this.ref('loading');
    const link = this.ref('product-link');

    // Update "View full details" link
    let url = `${window.Shopify.routes.root}products/${handle}`;
    if (variantId) url += `?variant=${variantId}`;
    if (link) link.href = url;

    // Show loading, open dialog
    loading.hidden = false;
    content.innerHTML = '';
    this.ref('dialog').showModal();

    // Check cache
    if (this.#cache.has(handle)) {
      this.#inject(this.#cache.get(handle), variantId);
      return;
    }

    // Fetch product section
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const fetchUrl = `${window.Shopify.routes.root}products/${handle}?section_id=main-product`;
      const res = await fetch(fetchUrl, { signal: this.#abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      // Cache with FIFO eviction
      if (this.#cache.size >= CACHE_MAX) {
        this.#cache.delete(this.#cache.keys().next().value);
      }
      this.#cache.set(handle, html);

      // Only inject if still the current request
      if (this.#currentHandle === handle) {
        this.#inject(html, variantId);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      loading.hidden = true;
      content.innerHTML = '<p style="text-align:center;padding:var(--space-8);color:rgb(var(--color-foreground-secondary))">Unable to load product</p>';
    }
  }

  #inject(html, variantId) {
    const content = this.ref('content');
    const loading = this.ref('loading');

    // Parse and extract section content
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('[class*="product-page"], .main-product, section > div');

    if (!section) {
      loading.hidden = true;
      return;
    }

    content.innerHTML = section.outerHTML;
    loading.hidden = true;

    // Re-execute module scripts (innerHTML doesn't execute scripts)
    for (const script of content.querySelectorAll('script[type="module"]')) {
      const clone = document.createElement('script');
      clone.type = 'module';
      if (script.src) clone.src = script.src;
      else clone.textContent = script.textContent;
      script.replaceWith(clone);
    }

    // Prevent variant picker from modifying page URL
    const picker = content.querySelector('product-variant-picker');
    if (picker) picker.setAttribute('data-skip-url-update', '');

    // Select variant if provided
    if (variantId) {
      requestAnimationFrame(() => {
        const input = content.querySelector(`input[type="radio"][value="${CSS.escape(String(variantId))}"]`);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  #close() {
    const dialog = this.ref('dialog');
    if (!dialog.open) return;
    dialog.close();
  }
}

if (!customElements.get('scale-quick-view')) {
  customElements.define('scale-quick-view', ScaleQuickView);
}
